import React, { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase/config";

const defaultContent = {
  bannerImage: "/authorcorner/banner.jpg",
  welcomeHeading: "Welcome to Simple Kindness!",
  introParagraphs: "I\u2019m so glad you\u2019re here. I founded Simple Kindness in May 2024 to bridge the gap in resources and education around mental health, inspired by the many individuals and families who often lack awareness of available support and how to access services. Together, we can create more meaningful change and ensure there are no \u201cfaces\u201d that struggle in isolation.\n\n\u201cLet\u2019s continue breaking the silence. My advocacy has written for the promise to give a voice to those who need it most.\u201d\n\nI hope this starts ongoing, open conversations about mental health, moving past stigma and toward understanding and support for everyone. Many of us have personal or family experiences with mental health issues, and together, we can build a compassionate community where everyone feels safe and heard.\n\nI stand against silence and support turning for mental health awareness. If you feel comfortable, I encourage you to share your story so that we can uplift each other.\n\nThank you for being part of this journey toward kindness and understanding.",
  signatureImage: "/authorcorner/signature.png",
  authorImage: "/authorcorner/author.jpg",
  authorFirstName: "Kristin Schmidt",
  authorLastName: "Larson",
  authorTitle: "Founder of Simple Kindness",
  linkedinText: "Follow me on LinkedIn",
  logoMarkImage: "/authorcorner/logo-mark.png",
  missionHeading: "Mission Statement",
  missionQuote: "\u201cOne act of kindness can change the world.\u201d",
  pillarsHeading: "Our Four Pillars",
  pillars: [
    { title: "Fostering Social Connections For Mental Health", description: "We prioritize the cultivation of meaningful social connections to enhance mental well-being and build a supportive community." },
    { title: "Encouraging Kindness And Generosity", description: "We advocate for acts of kindness and generosity, believing that small gestures can make a significant impact on individuals and society." },
    { title: "Promoting Compassion For Social Change", description: "We champion compassion as a driving force for positive social change, striving to create a more empathetic and inclusive world." },
    { title: "Advancing Holistic Self-Care", description: "We emphasize the importance of comprehensive self-care, encompassing mental, emotional, physical, environmental, spiritual, and social aspects, to foster overall well-being." },
  ],
  importantNote: "For each product sold, 2% will be donated to the National Alliance on Mental Illness (NAMI) \u2014 the nation\u2019s largest health organization dedicated to building better lives for millions of Americans affected by mental health.",
  subscribeHeading: "Get The Latest Updates",
  subscribePlaceholder: "Enter your email",
  subscribeButtonText: "Subscribe",
};

export default function Home() {
  const [content, setContent] = useState(defaultContent);

  useEffect(() => {
    const fetch = async () => {
      try {
        const snap = await getDoc(doc(db, "settings", "homeContent"));
        if (snap.exists()) {
          const data = snap.data();
          setContent((prev) => ({
            ...prev,
            ...data,
            pillars: data.pillars || prev.pillars,
          }));
        }
      } catch (e) {}
    };
    fetch();
  }, []);

  const paragraphs = content.introParagraphs.split("\n\n").filter(Boolean);

  return (
    <div className="bg-white text-black" style={{ fontFamily: "Montserrat, sans-serif" }}>
      {/* TOP BANNER */}
      <section className="w-full">
        <img src={content.bannerImage} alt="Traveling Caravan" className="w-full h-auto object-cover" />
      </section>

      {/* INTRO SECTION */}
      <section className="max-w-[1180px] mx-auto px-4 md:px-8 pt-10 md:pt-14 pb-8">
        <div className="grid lg:grid-cols-[1.25fr_0.75fr] gap-10 items-start">
          {/* LEFT */}
          <div>
            <div>
              <span className="text-[40px] leading-none">🌸</span>
              <h2 className="text-[14px] font-extrabold mt-1" style={{ fontFamily: "Open Sans, sans-serif", lineHeight: 1.5 }}>
                {content.welcomeHeading}
              </h2>
            </div>

            <div className="mt-5 text-[#565656] max-w-[650px]" style={{ fontFamily: "Open Sans, sans-serif", fontSize: "14px", lineHeight: 1.5 }}>
              {paragraphs.map((p, i) => (
                <p key={i} className={i > 0 ? "mt-5" : ""}>{p}</p>
              ))}

              <div className="mt-4">
                <div className="text-[16px] md:text-[18px] font-extrabold uppercase tracking-wide">
                  Love and Light,
                </div>
                <img src={content.signatureImage} alt="signature" className="mt-2 w-[130px] md:w-[150px] object-contain" />
              </div>
            </div>
          </div>

          {/* RIGHT */}
          <div className="flex flex-col items-center lg:items-end">
            <img src={content.authorImage} alt="Kristin Schmidt Larson" className="w-full max-w-[260px] object-cover" />

            <div className="mt-6 w-full max-w-[260px]">
              <h3 className="text-[20px] md:text-[22px] font-extrabold uppercase tracking-wide leading-tight">
                {content.authorFirstName}<br />{content.authorLastName}
              </h3>

              <div className="mt-3 text-[13px] md:text-[14px] font-extrabold uppercase tracking-wide text-black">
                {content.authorTitle}
              </div>

              <p className="mt-2 text-[10px] text-[#b58a72]">{content.linkedinText}</p>

              <div className="mt-10 flex justify-center lg:justify-end">
                <img src={content.logoMarkImage} alt="Simple Kindness" className="w-[160px] object-contain" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MISSION */}
      <section className="max-w-[1180px] mx-auto px-4 md:px-8 pt-10 pb-8">
        <div className="border-t border-gray-200 pt-8">
          <div className="text-center">
            <h2 className="text-[18px] md:text-[20px] font-extrabold uppercase tracking-wide">{content.missionHeading}</h2>
            <p className="mt-2 text-[11px] md:text-[12px] text-[#6b6b6b] italic">{content.missionQuote}</p>
            <h3 className="mt-4 text-[16px] md:text-[18px] font-extrabold uppercase tracking-wide">{content.pillarsHeading}</h3>
          </div>

          <div className="grid md:grid-cols-2 gap-x-12 gap-y-10 mt-10">
            {content.pillars.map((p, i) => (
              <div key={i}>
                <h4 className="text-[12px] md:text-[13px] font-extrabold uppercase tracking-wide">{p.title}</h4>
                <p className="mt-3 text-[11px] md:text-[12px] text-[#666] leading-[1.9]">{p.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* IMPORTANT NOTE */}
      <section className="bg-[#f8cfe0] mt-6">
        <div className="max-w-[1180px] mx-auto px-4 md:px-8 py-5 text-center">
          <div className="text-[14px] font-extrabold uppercase tracking-wide text-[#c45a72]" style={{ fontFamily: "Open Sans, sans-serif" }}>Important Note</div>
          <p className="mt-2 text-[14px] text-[#6a3b47]" style={{ fontFamily: "Open Sans, sans-serif", lineHeight: 1.5 }}>{content.importantNote}</p>
        </div>
      </section>

      {/* SUBSCRIBE */}
      <section className="bg-[#f4f4f4] border-t border-gray-200">
        <div className="max-w-[1180px] mx-auto px-4 md:px-8 py-6 md:py-8">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-6">
            <h3 className="text-[11px] md:text-[13px] font-extrabold tracking-wide uppercase">{content.subscribeHeading}</h3>
            <div className="flex w-full md:w-auto">
              <input type="email" placeholder={content.subscribePlaceholder}
                className="h-9 md:h-10 w-full md:w-[220px] border border-gray-300 bg-white px-3 text-[11px] outline-none" />
              <button className="h-9 md:h-10 px-5 bg-black text-white text-[10px] md:text-[11px] font-semibold uppercase">
                {content.subscribeButtonText}
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
